import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModalFocus } from './hooks/useModalFocus';
import { ApiError, api, apiErrorCode, imageLibraryItem } from './api';
import { BackupRecoveryPanel, type BackupDashboardView, type BackupRecordView } from './components/BackupRecoveryPanel';
import { ConfirmDialog, FolderMoveDialog, MoveDialog, NameDialog } from './components/FormDialog';
import { FolderTree } from './components/FolderTree';
import { Icon } from './components/Icon';
import { Login } from './components/Login';
import { MagicLinkSignIn } from './components/MagicLinkSignIn';
import { AccountLifecycle, type AccountLifecycleFlow } from './components/AccountLifecycle';
import { Brand } from './components/Brand';
import { HostedMembersPanel, type HostedMembersView } from './components/HostedMembersPanel';
import { ProcessingWorkersPanel, type ProcessingDashboardView } from './components/ProcessingWorkersPanel';
import { StatsPanel, type StorageDashboardView } from './components/StatsPanel';
import { TeamPanel } from './components/TeamPanel';
import { ThumbnailDialog, type ThumbnailSelection } from './components/ThumbnailDialog';
import { ToastRegion, type ToastMessage } from './components/Toast';
import { UploadModal } from './components/UploadModal';
import { VideoLibrary } from './components/VideoLibrary';
import { VideoPreview } from './components/VideoPreview';
import { ImagePreview } from './components/ImagePreview';
import { Modal } from './components/Modal';
import { QuotaHelp, quotaResourceForError } from './components/QuotaHelp';
import { iframeEmbedCode } from './embedCode';
import type { Folder, HostedMember, QuotaResource, Stats, SupportContext, User, VideoItem } from './types';
import { findFolder, folderPath, initials, isProcessing } from './utils';

type DialogState =
  | { type: 'none' }
  | { type: 'create-folder'; parentId: string | null }
  | { type: 'rename-folder'; folder: Folder }
  | { type: 'move-folder'; folder: Folder }
  | { type: 'delete-folder'; folder: Folder }
  | { type: 'thumbnail-video'; video: VideoItem }
  | { type: 'rename-video'; video: VideoItem }
  | { type: 'move-video'; video: VideoItem }
  | { type: 'delete-video'; video: VideoItem }
  | { type: 'purge-video'; video: VideoItem };

const emptyDialog: DialogState = { type: 'none' };

type AppSection = 'library' | 'trash' | 'storage' | 'processing' | 'backups' | 'team';

interface AppNavigationState {
  section: AppSection;
  backupDashboardView: BackupDashboardView;
  backupRecordView: BackupRecordView;
  processingView: ProcessingDashboardView;
  hostedMembersView: HostedMembersView;
  storageView: StorageDashboardView;
}

const APP_SECTIONS: AppSection[] = ['library', 'trash', 'storage', 'processing', 'backups', 'team'];
const BACKUP_DASHBOARD_VIEW_IDS: BackupDashboardView[] = ['backup', 'health', 'automation', 'storage-recovery', 'media-archive', 'records'];
const BACKUP_RECORD_VIEW_IDS: BackupRecordView[] = ['snapshots', 'restore-tests', 'activity'];
const PROCESSING_VIEW_IDS: ProcessingDashboardView[] = ['queue', 'availability', 'workers', 'setup'];
const HOSTED_MEMBER_VIEW_IDS: HostedMembersView[] = ['members', 'plans', 'orders', 'domains', 'invite'];
const STORAGE_VIEW_IDS: StorageDashboardView[] = ['overview', 'capacity', 'media', 'cleanup'];

function readNavigationState(): AppNavigationState {
  const routeParts = window.location.hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  const section = APP_SECTIONS.includes(routeParts[0] as AppSection) ? routeParts[0] as AppSection : 'library';
  const backupDashboardView = BACKUP_DASHBOARD_VIEW_IDS.includes(routeParts[1] as BackupDashboardView) ? routeParts[1] as BackupDashboardView : 'backup';
  const backupRecordView = BACKUP_RECORD_VIEW_IDS.includes(routeParts[2] as BackupRecordView) ? routeParts[2] as BackupRecordView : 'snapshots';
  const processingView = PROCESSING_VIEW_IDS.includes(routeParts[1] as ProcessingDashboardView) ? routeParts[1] as ProcessingDashboardView : 'queue';
  const hostedMembersView = HOSTED_MEMBER_VIEW_IDS.includes(routeParts[1] as HostedMembersView) ? routeParts[1] as HostedMembersView : 'members';
  const storageView = STORAGE_VIEW_IDS.includes(routeParts[1] as StorageDashboardView) ? routeParts[1] as StorageDashboardView : 'overview';

  return { section, backupDashboardView, backupRecordView, processingView, hostedMembersView, storageView };
}

function navigationHash(state: AppNavigationState): string {
  if (state.section === 'library') return '';
  const routeParts: string[] = [state.section];
  if (state.section === 'backups' && state.backupDashboardView !== 'backup') {
    routeParts.push(state.backupDashboardView);
    if (state.backupDashboardView === 'records' && state.backupRecordView !== 'snapshots') routeParts.push(state.backupRecordView);
  }
  if (state.section === 'processing' && state.processingView !== 'queue') routeParts.push(state.processingView);
  if (state.section === 'team' && state.hostedMembersView !== 'members') routeParts.push(state.hostedMembersView);
  if (state.section === 'storage' && state.storageView !== 'overview') routeParts.push(state.storageView);
  return `#/${routeParts.join('/')}`;
}

function writeNavigationState(state: AppNavigationState, replace = false) {
  const url = new URL(window.location.href);
  url.hash = navigationHash(state);

  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextUrl === currentUrl) return;
  if (replace) window.history.replaceState(null, '', nextUrl);
  else window.history.pushState(null, '', nextUrl);
}

function storedVideoView(): 'grid' | 'list' {
  try {
    return window.localStorage?.getItem('video-view') === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [magicSignInToken, setMagicSignInToken] = useState<string | null>(() =>
    new URLSearchParams(window.location.hash.slice(1)).get('sign-in'));
  useEffect(() => {
    if (magicSignInToken !== null) {
      // Keep the credential only in memory, never in persisted navigation or logs.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [magicSignInToken]);
  useEffect(() => {
    const captureSignInLink = () => {
      const token = new URLSearchParams(window.location.hash.slice(1)).get('sign-in');
      if (token !== null) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        setMagicSignInToken(token);
      }
    };
    window.addEventListener('hashchange', captureSignInLink);
    // Native fragment navigation may emit popstate before hashchange. Capture
    // before the dashboard navigation listener can normalize that fragment.
    window.addEventListener('popstate', captureSignInLink);
    return () => {
      window.removeEventListener('hashchange', captureSignInLink);
      window.removeEventListener('popstate', captureSignInLink);
    };
  }, []);
  const [accountLifecycleEnabled, setAccountLifecycleEnabled] = useState(false);
  const [publicSharePagesEnabled, setPublicSharePagesEnabled] = useState(false);
  const [imageHostingEnabled, setImageHostingEnabled] = useState(false);
  const [memberProvisioningEnabled, setMemberProvisioningEnabled] = useState(false);
  const [memberEntitlementsEnabled, setMemberEntitlementsEnabled] = useState(false);
  const [hostedProcessingSchedulingEnabled, setHostedProcessingSchedulingEnabled] = useState(false);
  const [memberOtpSessionsEmailChangeEnabled, setMemberOtpSessionsEmailChangeEnabled] = useState(false);
  const [memberPasswordBrowserTrustEnabled, setMemberPasswordBrowserTrustEnabled] = useState(false);
  const [activeSupportContext, setActiveSupportContext] = useState<SupportContext | null>(null);
  const [publicFlow, setPublicFlow] = useState<AccountLifecycleFlow | null>(() => {
    const query = new URLSearchParams(window.location.search);
    const flow = query.get('flow');
    const token = query.get('token') ?? '';
    const changeId = query.get('change') ?? '';
    if (flow === 'activate' && token) return { type: 'activation', token };
    if (flow === 'recover' && token) return { type: 'recovery-complete', token };
    if (flow === 'email-change' && changeId) return { type: 'email-change', changeId };
    return null;
  });
  const [folders, setFolders] = useState<Folder[]>([]);
  const [libraryMembers, setLibraryMembers] = useState<HostedMember[]>([]);
  const [selectedLibraryOwnerId, setSelectedLibraryOwnerId] = useState<string | null>(null);
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [mediaKind, setMediaKind] = useState<'all' | 'video' | 'image'>('all');
  const [mediaPage, setMediaPage] = useState(0);
  const [view, setView] = useState<'grid' | 'list'>(storedVideoView);
  const [navigation, setNavigation] = useState<AppNavigationState>(readNavigationState);
  const { section, backupDashboardView, backupRecordView, processingView, hostedMembersView, storageView } = navigation;
  const [videosLoading, setVideosLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [corePublicBrandingEnabled, setCorePublicBrandingEnabled] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileNavigation, setMobileNavigation] = useState(() => window.matchMedia?.('(max-width: 1050px)').matches ?? false);
  const sidebarRef = useRef<HTMLElement>(null);
  useModalFocus(sidebarRef, mobileNavigation && sidebarOpen && Boolean(user), () => setSidebarOpen(false));
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 1050px)');
    if (!media) return;
    const update = () => { setMobileNavigation(media.matches); setSidebarOpen(false); };
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState<VideoItem | null>(null);
  const [previewImage, setPreviewImage] = useState<VideoItem | null>(null);
  const [dialog, setDialog] = useState<DialogState>(emptyDialog);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [quotaHelp, setQuotaHelp] = useState<QuotaResource | null>(null);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const videosRequestRef = useRef(0);

  const canEdit = user?.role !== 'viewer';
  const isOwner = user?.role === 'owner';
  const hostedOwnerCatalog = Boolean(isOwner && memberProvisioningEnabled && !activeSupportContext);
  const canManageCurrentLibrary = Boolean(canEdit && !hostedOwnerCatalog);
  const selectedFolder = findFolder(folders, selectedFolderId);
  const breadcrumbs = folderPath(folders, selectedFolderId);
  const selectedLibraryMember = libraryMembers.find((member) => member.id === selectedLibraryOwnerId) ?? null;
  const childFolders = selectedFolder
    ? selectedFolder.children
    : hostedOwnerCatalog
      ? selectedLibraryOwnerId ? folders.filter((folder) => folder.ownerUserId === selectedLibraryOwnerId) : []
      : folders;

  const notify = useCallback((text: string, tone: ToastMessage['tone'] = 'success') => {
    const id = Date.now() + Math.round(Math.random() * 1000);
    setToasts((current) => [...current, { id, text, tone }]);
    window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4200);
  }, []);

  const handleError = useCallback((reason: unknown, fallback: string) => {
    if (reason instanceof ApiError && reason.status === 401) {
      api.clearSupportContext();
      setActiveSupportContext(null);
      setUser(null);
      setQuotaHelp(null);
      setFolders([]);
      setVideos([]);
      notify('Your session expired. Please sign in again.', 'error');
      return;
    }
    notify(reason instanceof ApiError ? reason.message : fallback, 'error');
  }, [notify]);

  const loadFolders = useCallback(async () => {
    try {
      setFolders(await api.folders({ memberCatalog: hostedOwnerCatalog }));
    } catch (reason) {
      handleError(reason, 'Unable to load folders.');
    }
  }, [handleError, hostedOwnerCatalog]);

  const loadVideos = useCallback(async (quiet = false) => {
    if (!user) return;
    const requestId = ++videosRequestRef.current;
    if (!quiet) setVideosLoading(true);
    try {
      const scope: 'active' | 'trash' = section === 'trash' ? 'trash' : 'active';
      const filters = {
        folderId: selectedFolderId,
        search,
        status,
        scope,
        ownerUserId: hostedOwnerCatalog ? selectedLibraryOwnerId : null,
        memberCatalog: hostedOwnerCatalog,
      };
      const result = imageHostingEnabled ? await api.media({ ...filters, kind: mediaKind, offset: mediaPage * 100 }) : await api.videos(filters);
      if (requestId !== videosRequestRef.current) return;
      setVideos(result.videos);
      setTotal(result.total);
      if (imageHostingEnabled && mediaPage > Math.max(0, Math.ceil(result.total / 100) - 1)) setMediaPage(Math.max(0, Math.ceil(result.total / 100) - 1));
    } catch (reason) {
      if (requestId === videosRequestRef.current) handleError(reason, 'Unable to load videos.');
    } finally {
      if (requestId === videosRequestRef.current) setVideosLoading(false);
    }
  }, [handleError, hostedOwnerCatalog, imageHostingEnabled, mediaKind, mediaPage, search, section, selectedFolderId, selectedLibraryOwnerId, status, user]);

  useEffect(() => { setMediaPage(0); }, [mediaKind, search, status, section, selectedFolderId, selectedLibraryOwnerId]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      setStats(await api.stats({ installation: isOwner && !activeSupportContext }));
    } catch (reason) {
      handleError(reason, 'Unable to load storage statistics.');
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [activeSupportContext, handleError, isOwner]);

  useEffect(() => {
    let active = true;
    api.clearSupportContext();
    Promise.all([
      api.me().then((account) => { if (active) setUser(account); }).catch(() => undefined),
      api.capabilities().then((capabilities) => {
        if (!active) return;
        setAccountLifecycleEnabled(capabilities.accountLifecycle);
        setPublicSharePagesEnabled(Boolean(capabilities.publicSharePages));
        setImageHostingEnabled(Boolean(capabilities.imageHosting));
        setCorePublicBrandingEnabled(Boolean(capabilities.corePublicBranding));
        setMemberProvisioningEnabled(capabilities.memberProvisioning);
        setMemberEntitlementsEnabled(Boolean(capabilities.memberEntitlements));
        setHostedProcessingSchedulingEnabled(Boolean(capabilities.hostedProcessingScheduling));
        setMemberOtpSessionsEmailChangeEnabled(Boolean(capabilities.memberOtpSessionsEmailChange));
        setMemberPasswordBrowserTrustEnabled(Boolean(capabilities.memberPasswordBrowserTrust));
      }).catch(() => undefined),
    ]).finally(() => { if (active) setCheckingSession(false); });
    return () => { active = false; };
  }, []);

  function showPublicFlow(flow: AccountLifecycleFlow | null) {
    const url = new URL(window.location.href);
    url.searchParams.delete('flow');
    url.searchParams.delete('token');
    url.searchParams.delete('change');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    setPublicFlow(flow);
  }

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!user) return;
    void loadFolders();
  }, [activeSupportContext?.memberId, loadFolders, user]);

  useEffect(() => {
    if (!user || !hostedOwnerCatalog) {
      setLibraryMembers([]);
      return;
    }
    let active = true;
    api.hostedMembers().then((members) => {
      if (active) setLibraryMembers(members);
    }).catch((reason) => {
      if (active) handleError(reason, 'Unable to load Member libraries.');
    });
    return () => { active = false; };
  }, [handleError, hostedOwnerCatalog, user]);

  useEffect(() => {
    const restoreNavigation = () => setNavigation(readNavigationState());
    window.addEventListener('popstate', restoreNavigation);
    window.addEventListener('hashchange', restoreNavigation);
    return () => {
      window.removeEventListener('popstate', restoreNavigation);
      window.removeEventListener('hashchange', restoreNavigation);
    };
  }, []);

  useEffect(() => {
    writeNavigationState(navigation, true);
  }, [navigation]);

  useEffect(() => {
    if (!user || isOwner || !['processing', 'backups'].includes(section)) return;
    const nextNavigation = { ...navigation, section: 'library' as const };
    setNavigation(nextNavigation);
    writeNavigationState(nextNavigation, true);
  }, [isOwner, navigation, section, user]);

  useEffect(() => {
    if (!user || canManageCurrentLibrary || section !== 'trash') return;
    const nextNavigation = { ...navigation, section: 'library' as const };
    setNavigation(nextNavigation);
    writeNavigationState(nextNavigation, true);
  }, [canManageCurrentLibrary, navigation, section, user]);

  useEffect(() => {
    if (!user || (section !== 'library' && section !== 'trash')) return;
    void loadVideos();
    return () => { videosRequestRef.current += 1; };
  }, [loadVideos, section, user]);

  useEffect(() => {
    if (!user) return;
    void loadStats();
    const timer = window.setInterval(() => void loadStats(), 60_000);
    return () => window.clearInterval(timer);
  }, [activeSupportContext?.memberId, loadStats, user]);

  const hasProcessingVideos = useMemo(() => videos.some((video) => isProcessing(video) || ['probing', 'transcoding', 'validating', 'resolving', 'downloading', 'copying', 'importing', 'publishing', 'finalizing'].includes(video.status)), [videos]);
  useEffect(() => {
    if (!user || (section !== 'library' && section !== 'trash') || !hasProcessingVideos) return;
    let active = true;
    let timer = 0;
    const poll = async (): Promise<void> => {
      await loadVideos(true);
      if (active) timer = window.setTimeout(() => void poll(), 5000);
    };
    timer = window.setTimeout(() => void poll(), 5000);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [hasProcessingVideos, loadVideos, section, user]);

  const previewIsProcessing = Boolean(previewVideo && isProcessing(previewVideo));
  useEffect(() => {
    const videoId = previewVideo?.id;
    if (!user || !videoId || !previewIsProcessing) return;
    let active = true;
    let timer = 0;
    const poll = async (): Promise<void> => {
      try {
        const updated = await api.video(videoId);
        if (!active) return;
        setPreviewVideo((current) => current?.id === videoId ? updated : current);
        setVideos((current) => current.map((video) => video.id === videoId ? updated : video));
      } catch (reason) {
        if (!active) return;
        if (reason instanceof ApiError && reason.status === 401) {
          active = false;
          handleError(reason, 'Your session expired.');
          return;
        }
      }
      if (active) timer = window.setTimeout(() => void poll(), 5000);
    };
    void poll();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [handleError, previewIsProcessing, previewVideo?.id, user]);

  function closeDialog() {
    if (dialogBusy) return;
    setDialog(emptyDialog);
    setDialogError('');
  }

  async function runDialogMutation(action: () => Promise<void>) {
    setDialogBusy(true);
    setDialogError('');
    try {
      await action();
      setDialog(emptyDialog);
      await Promise.all([loadFolders(), loadVideos(true)]);
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 401) handleError(reason, 'Your session expired.');
      else setDialogError(reason instanceof ApiError ? reason.message : 'The change could not be saved.');
    } finally {
      setDialogBusy(false);
    }
  }

  async function createFolder(name: string) {
    if (dialog.type !== 'create-folder') return;
    await runDialogMutation(async () => {
      await api.createFolder(name, dialog.parentId);
      notify('Folder created.');
    });
  }

  async function renameFolder(name: string) {
    if (dialog.type !== 'rename-folder') return;
    await runDialogMutation(async () => {
      await api.updateFolder(dialog.folder.id, { name });
      notify('Folder renamed.');
    });
  }

  async function moveFolder(parentId: string | null) {
    if (dialog.type !== 'move-folder') return;
    await runDialogMutation(async () => {
      await api.updateFolder(dialog.folder.id, { parentId });
      notify('Folder moved.');
    });
  }

  async function deleteFolder(recursive: boolean) {
    if (dialog.type !== 'delete-folder') return;
    const deletingSelected = dialog.folder.id === selectedFolderId || breadcrumbs.some((folder) => folder.id === dialog.folder.id);
    await runDialogMutation(async () => {
      await api.deleteFolder(dialog.folder.id, recursive);
      if (deletingSelected) setSelectedFolderId(dialog.folder.parentId);
      notify('Folder deleted.');
    });
  }

  async function changeThumbnail(selection: ThumbnailSelection) {
    if (dialog.type !== 'thumbnail-video') return;
    const target = dialog.video;
    await runDialogMutation(async () => {
      const updated = selection.type === 'frame'
        ? await api.updateVideoThumbnailFromVideo(target.id, selection.timeSeconds)
        : await api.updateVideoThumbnail(target.id, selection.file);
      setPreviewVideo((current) => current?.id === target.id ? updated : current);
      notify('Thumbnail updated.');
    });
  }

  async function renameVideo(title: string) {
    if (dialog.type !== 'rename-video') return;
    const target = dialog.video;
    await runDialogMutation(async () => {
      const updated = target.mediaKind === 'image' ? imageLibraryItem(await api.updateImage(target.id, { name: title })) : await api.updateVideo(target.id, { title });
      setPreviewImage((current) => current?.id === target.id ? updated : current);
      setPreviewVideo((current) => current?.id === target.id ? updated : current);
      notify(target.mediaKind === 'image' ? 'Image renamed.' : 'Video renamed.');
    });
  }

  async function moveVideo(folderId: string | null) {
    if (dialog.type !== 'move-video') return;
    const target = dialog.video;
    await runDialogMutation(async () => {
      const updated = target.mediaKind === 'image' ? imageLibraryItem(await api.updateImage(target.id, { folderId })) : await api.updateVideo(target.id, { folderId });
      setPreviewImage((current) => current?.id === target.id ? updated : current);
      setPreviewVideo((current) => current?.id === target.id ? updated : current);
      notify(target.mediaKind === 'image' ? 'Image moved.' : 'Video moved.');
    });
  }

  async function deleteVideo() {
    if (dialog.type !== 'delete-video') return;
    const target = dialog.video;
    await runDialogMutation(async () => {
      if (target.mediaKind === 'image') await api.imageAction(target.id, 'trash');
      else await api.deleteVideo(target.id);
      setPreviewImage((current) => current?.id === target.id ? null : current);
      setPreviewVideo((current) => current?.id === target.id ? null : current);
      notify(target.mediaKind === 'image' ? 'Image moved to trash.' : 'Video moved to trash.');
    });
  }

  async function purgeVideo() {
    if (dialog.type !== 'purge-video') return;
    await runDialogMutation(async () => {
      if (dialog.video.mediaKind === 'image') await api.purgeImage(dialog.video.id);
      else await api.purgeVideo(dialog.video.id);
      notify(dialog.video.mediaKind === 'image' ? 'Image permanently deleted.' : 'Video permanently deleted.');
    });
  }

  async function restoreVideo(video: VideoItem) {
    try {
      if (video.mediaKind === 'image') await api.imageAction(video.id, 'restore');
      else await api.restoreVideo(video.id);
      notify(video.mediaKind === 'image' ? 'Image restored.' : 'Video restored.');
      await loadVideos(true);
    } catch (reason) {
      handleError(reason, 'Unable to restore the video.');
    }
  }

  async function retryVideo(video: VideoItem) {
    try {
      const updated = video.mediaKind === 'image' ? imageLibraryItem(await api.imageAction(video.id, 'retry')) : await api.retryVideo(video.id);
      setPreviewVideo((current) => current?.id === video.id ? updated : current);
      notify(video.mediaKind === 'image' ? 'Image queued for optimization.' : 'Video queued for another processing attempt.');
      await loadVideos(true);
    } catch (reason) {
      const resource = quotaResourceForError(apiErrorCode(reason));
      if (memberEntitlementsEnabled && !isOwner && resource) {
        setPreviewVideo(null);
        setQuotaHelp(resource);
        return;
      }
      handleError(reason, 'Unable to retry processing.');
    }
  }

  async function copyIframeEmbed(video: VideoItem) {
    try {
      await navigator.clipboard.writeText(iframeEmbedCode(video, window.location.origin));
      notify('Iframe embed code copied to clipboard.');
    } catch (reason) {
      handleError(reason, 'Unable to copy the iframe embed code.');
    }
  }

  function openMedia(item: VideoItem) {
    if (item.mediaKind === 'image') setPreviewImage(item);
    else setPreviewVideo(item);
  }
  const mixedLibraryProps = imageHostingEnabled ? { mediaKind, onMediaKind: setMediaKind, page: mediaPage, onPage: setMediaPage, readOnlyImages: Boolean(activeSupportContext) } : {};
  const editablePreviewImage = canManageCurrentLibrary && !activeSupportContext;
  useEffect(() => { setPreviewImage(null); }, [user?.id, activeSupportContext?.memberId]);

  const refreshAfterUpload = useCallback((message?: string, tone: ToastMessage['tone'] = 'success') => {
    if (message) notify(message, tone);
    void Promise.all([loadFolders(), loadVideos(true)]);
  }, [loadFolders, loadVideos, notify]);

  async function logout() {
    try {
      await api.logout();
    } catch {
      // Clear local UI state even when the server session is already gone.
    }
    setUser(null);
    setQuotaHelp(null);
    api.clearSupportContext();
    setActiveSupportContext(null);
    setFolders([]);
    setVideos([]);
  }

  function enterSupportContext(context: SupportContext) {
    videosRequestRef.current += 1;
    setActiveSupportContext(context);
    setFolders([]);
    setVideos([]);
    setStats(null);
    setSelectedFolderId(null);
    setSelectedLibraryOwnerId(null);
    setSearchInput('');
    setSearch('');
    setStatus('all');
    navigate('library');
  }

  function exitSupportContext() {
    videosRequestRef.current += 1;
    api.clearSupportContext();
    setActiveSupportContext(null);
    setFolders([]);
    setVideos([]);
    setStats(null);
    setSelectedFolderId(null);
    setSelectedLibraryOwnerId(null);
    setSearchInput('');
    setSearch('');
    setStatus('all');
    navigate('library');
    notify('Returned to the Owner library.');
  }

  function navigate(section: AppSection, nextBackupDashboardView = backupDashboardView, nextBackupRecordView = backupRecordView, nextProcessingView = processingView, nextHostedMembersView = hostedMembersView, nextStorageView = storageView) {
    const nextNavigation = {
      section,
      backupDashboardView: nextBackupDashboardView,
      backupRecordView: nextBackupRecordView,
      processingView: nextProcessingView,
      hostedMembersView: nextHostedMembersView,
      storageView: nextStorageView,
    };
    setNavigation(nextNavigation);
    writeNavigationState(nextNavigation);
    setSidebarOpen(false);
  }

  function selectFolder(id: string | null) {
    setSelectedFolderId(id);
    if (hostedOwnerCatalog) {
      const folder = id ? findFolder(folders, id) : null;
      setSelectedLibraryOwnerId(folder?.ownerUserId ?? null);
    }
    setSearchInput('');
    setSearch('');
    setStatus('all');
    navigate('library');
  }

  function selectMemberLibrary(id: string) {
    setSelectedLibraryOwnerId(id);
    setSelectedFolderId(null);
    setSearchInput('');
    setSearch('');
    setStatus('all');
    navigate('library');
  }

  function changeView(nextView: 'grid' | 'list') {
    setView(nextView);
    try {
      window.localStorage?.setItem('video-view', nextView);
    } catch {
      // Private browsing and embedded contexts may disable persistent storage.
    }
  }

  if (checkingSession) return <div className="app-loading"><Brand /><span className="spinner spinner--large" /><p>Opening your library…</p></div>;
  if (magicSignInToken !== null && memberOtpSessionsEmailChangeEnabled) return <MagicLinkSignIn browserTrustEnabled={memberPasswordBrowserTrustEnabled} token={magicSignInToken} currentUser={user} onCancel={() => setMagicSignInToken(null)} onAuthenticated={(account) => { setMagicSignInToken(null); setUser(account); }} />;
  if (!user && publicFlow && accountLifecycleEnabled) return <AccountLifecycle flow={publicFlow} onSignIn={() => showPublicFlow(null)} />;
  if (!user) return <Login memberPasswordBrowserTrust={memberPasswordBrowserTrustEnabled} memberOtpEnabled={memberOtpSessionsEmailChangeEnabled} onAuthenticated={setUser} onForgotPassword={accountLifecycleEnabled ? () => showPublicFlow({ type: 'recovery-request' }) : undefined} />;

  return <div className="app-shell">
    <header className="mobile-header"><button type="button" className="icon-button" onClick={() => setSidebarOpen(true)} aria-label="Open navigation" aria-expanded={sidebarOpen} aria-controls="app-navigation"><Icon name="bars" /></button><Brand /><span className="avatar">{initials(user.displayName)}</span></header>
    {mobileNavigation && sidebarOpen && <div className="sidebar-scrim" data-modal-backdrop aria-hidden="true" onClick={() => setSidebarOpen(false)} />}
    <aside id="app-navigation" ref={sidebarRef} tabIndex={-1} inert={mobileNavigation && !sidebarOpen} role={mobileNavigation ? "dialog" : undefined} aria-modal={mobileNavigation && sidebarOpen ? true : undefined} aria-label="Navigation" className={`sidebar ${sidebarOpen ? 'is-open' : ''}`}>
      <div className="sidebar__top"><Brand /><button type="button" className="icon-button sidebar__close" onClick={() => setSidebarOpen(false)} aria-label="Close navigation"><Icon name="close" /></button></div>
      <nav className="primary-nav" aria-label="Main navigation">
        <button type="button" className={section === 'library' ? 'is-active' : ''} onClick={() => navigate('library')}><Icon name="film" size={19} />Library</button>
        {canManageCurrentLibrary && <button type="button" className={section === 'trash' ? 'is-active' : ''} onClick={() => navigate('trash')}><Icon name="archive" size={19} />Trash</button>}
        <button type="button" className={section === 'storage' ? 'is-active' : ''} onClick={() => navigate('storage')}><Icon name="hard-drive" size={19} />Storage & stats</button>
        {isOwner && <button type="button" className={section === 'processing' ? 'is-active' : ''} onClick={() => navigate('processing')}><Icon name="server" size={19} />Processing</button>}
        {isOwner && <button type="button" className={section === 'backups' ? 'is-active' : ''} onClick={() => navigate('backups')}><Icon name="shield" size={19} />Backup & Recovery</button>}
        <button type="button" className={section === 'team' ? 'is-active' : ''} onClick={() => navigate('team')}><Icon name="settings" size={19} />{user.role === 'member' ? 'Account' : 'Team & account'}</button>
      </nav>
      <div className="sidebar__section-heading"><span>{hostedOwnerCatalog ? 'Member libraries' : 'Folders'}</span>{canManageCurrentLibrary && <button type="button" onClick={() => setDialog({ type: 'create-folder', parentId: selectedFolderId })} aria-label="Create folder"><Icon name="plus" size={17} /></button>}</div>
      <div className="sidebar__folders"><FolderTree imageHosting={imageHostingEnabled} folders={folders} selectedId={selectedFolderId} onSelect={selectFolder} onCreate={(parentId) => setDialog({ type: 'create-folder', parentId })} onRename={(folder) => setDialog({ type: 'rename-folder', folder })} onMove={(folder) => setDialog({ type: 'move-folder', folder })} onDelete={(folder) => setDialog({ type: 'delete-folder', folder })} canEdit={canManageCurrentLibrary && (!mobileNavigation || sidebarOpen)} memberLibraries={hostedOwnerCatalog ? libraryMembers : undefined} selectedMemberId={selectedLibraryOwnerId} onMemberSelect={hostedOwnerCatalog ? selectMemberLibrary : undefined} /></div>
      <div className="sidebar__account"><span className="avatar">{initials(user.displayName)}</span><div><strong>{user.displayName}</strong><span>{user.email}</span></div><button type="button" className="icon-button icon-button--quiet" onClick={logout} aria-label="Sign out"><Icon name="logout" size={18} /></button></div>
    </aside>
    <main className="main-content">
      {activeSupportContext && <div className="support-context-banner" role="status"><div><strong>Supporting {activeSupportContext.memberName}</strong><span>{activeSupportContext.memberEmail} · {activeSupportContext.reason}</span></div><button className="button button--secondary" type="button" onClick={exitSupportContext}>Exit to Owner library</button></div>}
      {stats && stats.diskLevel !== 'normal' && <div className={`system-alert system-alert--${stats.diskLevel} system-alert--global`}><Icon name="warning" size={18} /><div><strong>Disk {Math.round(stats.diskPercent)}% full</strong><span>{stats.diskLevel === 'reject' ? 'New uploads are blocked until storage is freed.' : `Uploads will be blocked at ${stats.diskThresholds.rejectPercent}%.`}</span></div></div>}
      {section === 'library' ? <>
        <nav className="breadcrumbs" aria-label="Breadcrumb"><button type="button" onClick={() => selectFolder(null)}>Library</button>{hostedOwnerCatalog && selectedLibraryMember && <span><Icon name="chevron" size={13} /><button type="button" onClick={() => selectMemberLibrary(selectedLibraryMember.id)}>{selectedLibraryMember.displayName}</button></span>}{breadcrumbs.map((folder) => <span key={folder.id}><Icon name="chevron" size={13} /><button type="button" onClick={() => selectFolder(folder.id)}>{folder.name}</button></span>)}</nav>
        <VideoLibrary {...mixedLibraryProps} videos={videos} childFolders={childFolders} title={selectedFolder?.name ?? (selectedLibraryMember ? `${selectedLibraryMember.displayName} ${imageHostingEnabled ? 'media' : 'videos'}` : imageHostingEnabled ? 'All media' : 'All videos')} total={total} loading={videosLoading} search={searchInput} status={status} view={view} canEdit={canManageCurrentLibrary} canOpen={!hostedOwnerCatalog} showOwner={hostedOwnerCatalog} onSearch={setSearchInput} onStatus={setStatus} onView={changeView} onFolderSelect={selectFolder} onUpload={() => setUploadOpen(true)} onPreview={openMedia} onRename={(video) => setDialog({ type: 'rename-video', video })} onMove={(video) => setDialog({ type: 'move-video', video })} onThumbnail={(video) => { setPreviewVideo(video); setDialog({ type: 'thumbnail-video', video }); }} onCopyIframe={copyIframeEmbed} onDelete={(video) => setDialog({ type: 'delete-video', video })} onRetry={retryVideo} onRefresh={() => void loadVideos()} />
      </> : section === 'trash' && canManageCurrentLibrary ? <>
        <VideoLibrary {...mixedLibraryProps} scope="trash" videos={videos} childFolders={[]} title="Trash" total={total} loading={videosLoading} search={searchInput} status={status} view={view} canEdit={canManageCurrentLibrary} onSearch={setSearchInput} onStatus={setStatus} onView={changeView} onFolderSelect={() => undefined} onUpload={() => setUploadOpen(true)} onPreview={openMedia} onRename={() => undefined} onMove={() => undefined} onThumbnail={() => undefined} onCopyIframe={copyIframeEmbed} onDelete={(video) => setDialog({ type: 'purge-video', video })} onRetry={retryVideo} onRestore={restoreVideo} onRefresh={() => void loadVideos()} />
</> : section === 'storage' ? <StatsPanel stats={stats} loading={statsLoading} isOwner={isOwner} hostedEntitlementsEnabled={memberEntitlementsEnabled} view={storageView} onViewChange={(nextView) => navigate('storage', backupDashboardView, backupRecordView, processingView, hostedMembersView, nextView)} notify={notify} /> : section === 'processing' && isOwner ? <ProcessingWorkersPanel notify={notify} hostedSchedulingEnabled={hostedProcessingSchedulingEnabled} view={processingView} onViewChange={(nextView) => navigate('processing', backupDashboardView, backupRecordView, nextView)} /> : section === 'backups' && isOwner ? <BackupRecoveryPanel imageHostingEnabled={imageHostingEnabled} notify={notify} dashboardView={backupDashboardView} recordView={backupRecordView} onViewChange={(nextDashboardView, nextRecordView) => navigate('backups', nextDashboardView, nextRecordView)} hostedMemberFiltering={memberProvisioningEnabled} onOpenVideo={(videoId) => { void api.video(videoId).then((video) => { navigate('library'); setPreviewVideo(video); }).catch((reason) => handleError(reason, 'Unable to open the archived video.')); }} /> : memberProvisioningEnabled ? <HostedMembersPanel currentUser={user} notify={notify} onOpenVideo={async (id) => { setPreviewVideo(await api.video(id)); }} onEnterSupport={enterSupportContext} memberOtpSessionsEmailChangeEnabled={memberOtpSessionsEmailChangeEnabled} memberPasswordBrowserTrustEnabled={memberPasswordBrowserTrustEnabled} view={hostedMembersView} onViewChange={(nextView) => navigate('team', backupDashboardView, backupRecordView, processingView, nextView)} /> : <TeamPanel currentUser={user} notify={notify} onOpenVideo={async (id) => { setPreviewVideo(await api.video(id)); }} corePublicBrandingEnabled={corePublicBrandingEnabled} />}
    </main>

    <UploadModal open={uploadOpen} folderId={selectedFolderId} folderName={selectedFolder?.name ?? 'the library root'} folders={folders} imageHostingEnabled={imageHostingEnabled && !activeSupportContext} hostedEntitlementsEnabled={memberEntitlementsEnabled} onClose={() => setUploadOpen(false)} onUploaded={refreshAfterUpload} />
    <ImagePreview item={previewImage} canEdit={editablePreviewImage} sharingReadOnly={Boolean(activeSupportContext)} publicSharePagesEnabled={publicSharePagesEnabled} folderName={previewImage?.folderId ? findFolder(folders, previewImage.folderId)?.name ?? 'Unknown folder' : 'Library root'} onClose={() => setPreviewImage(null)} onMove={item => setDialog({ type: 'move-video', video: item })} onDelete={item => setDialog({ type: 'delete-video', video: item })} onCopied={notify} onUpdated={item => { setPreviewImage(current => current?.id === item.id ? item : current); setVideos(current => current.map(existing => existing.id === item.id ? item : existing)); }} />
    <VideoPreview
      video={previewVideo}
      canEdit={canManageCurrentLibrary}
      publicSharePagesEnabled={publicSharePagesEnabled}
      memberPublicBrandingEnabled={memberProvisioningEnabled && user.role === 'member' && !activeSupportContext}
      sharingReadOnly={Boolean(activeSupportContext)}
      folderName={previewVideo?.folderId ? findFolder(folders, previewVideo.folderId)?.name ?? 'Unknown folder' : 'Library root'}
      onClose={() => setPreviewVideo(null)}
      onCopied={(message) => notify(message)}
      onThumbnail={(video) => setDialog({ type: 'thumbnail-video', video })}
      onMove={(video) => setDialog({ type: 'move-video', video })}
      onDelete={(video) => setDialog({ type: 'delete-video', video })}
      onRetry={(video) => void retryVideo(video)}
      onUpdated={(video) => {
        setPreviewVideo(video);
        setVideos((current) => current.map((item) => item.id === video.id ? video : item));
      }}
    />
    <ThumbnailDialog video={dialog.type === 'thumbnail-video' ? dialog.video : null} busy={dialogBusy} error={dialogError} onClose={closeDialog} loadOptions={api.getVideoThumbnailOptions} onSubmit={changeThumbnail} />
    <NameDialog open={dialog.type === 'create-folder'} title="Create folder" description={`Add a folder inside ${selectedFolder?.name ?? 'the library root'}.`} label="Folder name" submitLabel="Create folder" busy={dialogBusy} error={dialogError} onClose={closeDialog} onSubmit={createFolder} />
    <NameDialog open={dialog.type === 'rename-folder'} title="Rename folder" description="Choose a clear name for this folder." label="Folder name" initialValue={dialog.type === 'rename-folder' ? dialog.folder.name : ''} submitLabel="Save name" busy={dialogBusy} error={dialogError} onClose={closeDialog} onSubmit={renameFolder} />
    <FolderMoveDialog open={dialog.type === 'move-folder'} folder={dialog.type === 'move-folder' ? dialog.folder : null} folders={folders} busy={dialogBusy} error={dialogError} onClose={closeDialog} onSubmit={moveFolder} />
    <NameDialog open={dialog.type === 'rename-video'} title={dialog.type === 'rename-video' && dialog.video.mediaKind === 'image' ? 'Rename image' : 'Rename video'} description="This changes the library title, not its media URL." label={dialog.type === 'rename-video' && dialog.video.mediaKind === 'image' ? 'Image name' : 'Video title'} initialValue={dialog.type === 'rename-video' ? dialog.video.title : ''} submitLabel="Save title" busy={dialogBusy} error={dialogError} onClose={closeDialog} onSubmit={renameVideo} />
    <MoveDialog itemKind={dialog.type === 'move-video' && dialog.video.mediaKind === 'image' ? 'image' : 'video'} open={dialog.type === 'move-video'} videoTitle={dialog.type === 'move-video' ? dialog.video.title : ''} folders={folders} currentFolderId={dialog.type === 'move-video' ? dialog.video.folderId : null} busy={dialogBusy} error={dialogError} onClose={closeDialog} onSubmit={moveVideo} />
    <ConfirmDialog open={dialog.type === 'delete-folder'} title="Delete folder?" description={dialog.type === 'delete-folder' ? `“${dialog.folder.name}” will be removed. Empty folders can be deleted directly.` : ''} confirmLabel="Delete folder" busy={dialogBusy} error={dialogError} recursiveOption onClose={closeDialog} onConfirm={deleteFolder} />
    <ConfirmDialog open={dialog.type === 'delete-video'} title="Move to trash?" description={dialog.type === 'delete-video' ? `“${dialog.video.title}” will be moved to Trash. Its fixed recovery deadline will be shown there; moving it does not free storage quota.` : ''} confirmLabel="Move to trash" busy={dialogBusy} error={dialogError} onClose={closeDialog} onConfirm={deleteVideo} />
    <ConfirmDialog open={dialog.type === 'purge-video'} title="Delete permanently?" description={dialog.type === 'purge-video' ? `“${dialog.video.title}” will be removed from the library and cannot be restored from Trash. This bypasses any remaining Trash recovery time. Installation disaster-recovery backups are separate and do not extend the customer recovery deadline.` : ''} confirmLabel="Delete permanently" busy={dialogBusy} error={dialogError} onClose={closeDialog} onConfirm={purgeVideo} />
    <Modal open={Boolean(quotaHelp)} title="Allowance is insufficient" onClose={() => setQuotaHelp(null)} width="small" footer={<button type="button" className="button button--secondary" onClick={() => setQuotaHelp(null)}>Close</button>}>
      {quotaHelp && <QuotaHelp resource={quotaHelp} />}
    </Modal>
    <ToastRegion toasts={toasts} dismiss={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))} />
  </div>;
}
